import { PayableRow, PayableStatus } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { payablesApi } from "@api";
import { queriesPayableRows, queriesPayables } from "@query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";
import { QUERY_PAYABLE } from "../../../query/payments/keys";

const invalidateAfterMutation = (successItems: PayableRow[]) => {
  WSQueryCache.invalidateQueries(
    queriesPayableRows.searchPayableRows.queryKeyBase
  );
  WSQueryCache.invalidateQueries(queriesPayables.getPayable.queryKeyBase);
  successItems.forEach(payable => {
    if (payable.id) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.id]);
    }
  });
};

const processDeletePayables = (
  items: PayableRow[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const bulkAction = bulkActionFactory<PayableRow>({
    onProgress,
    action: async item => {
      if (!item.id) {
        throw new Error("Payable is missing id");
      }
      if (item.status !== PayableStatus.CREATED) {
        throw new Error(
          `Cannot delete payable with status "${item.status}". Only draft payables can be deleted.`
        );
      }
      return payablesApi.deletePayable({ payableId: item.id });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(items).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkDeletePayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayableRow[], onSuccess?: () => void) => {
    const count = items.length;
    const pluralized = count > 1 ? "payables" : "payable";

    return runBulkAction({
      actionName: "Delete payables",
      itemCount: count,
      confirmDescription: `Are you sure you want to permanently delete ${count.toLocaleString()} draft ${pluralized}? This action cannot be undone.`,
      confirmLabel: "Delete",
      confirmDestructive: true,
      prepare: async () => items,
      process: async (data, onProgress) =>
        processDeletePayables(data, onProgress, onSuccess)
    });
  };
};
